import { PAID_TO_CUSTOMER } from "./consts";
import { ConsolidationItem } from "./types";

/**
 * Default purpose text for a settlement cash-out row
 */
export const getDefaultSettlePurpose = (c: ConsolidationItem): string => {
  const name = (c.provider ?? "").trim();
  return name ? `To settle ${name}` : `To settle ${c.providerType}`;
};

/**
 * Keys for cash-out purpose fields
 */
export const getCashOutPurposeRowKeys = (
  creditProviders: ConsolidationItem[],
  loanAmount: number,
  totalConsolidated: number,
): string[] => {
  const remainingCash = loanAmount - totalConsolidated;
  const cashToCustomerItems = creditProviders.filter(
    (c) => c.paidTo === PAID_TO_CUSTOMER,
  );
  const settlementKeys = cashToCustomerItems.map((c) => `settle-${c.uuid}`);
  const includeRemainingCashOutRow =
    remainingCash > 0 ||
    (settlementKeys.length === 0 && loanAmount > totalConsolidated);

  if (includeRemainingCashOutRow) {
    return [...settlementKeys, "remaining"];
  }
  return settlementKeys;
};

export const getCashOutItemsTotal = (creditProviders: ConsolidationItem[]) => {
  const cashToCustomerItems = creditProviders.filter(
    (c) => c.paidTo === PAID_TO_CUSTOMER,
  );
  return cashToCustomerItems.reduce(
    (s, c) => s + Number(c.settlementAmount || 0),
    0,
  );
};

/**
 * Total cash out (cash-to-customer rows + remaining balance)
 */
export const getTotalCashOut = (
  creditProviders: ConsolidationItem[],
  loanAmount: number,
): number => {
  const totalConsolidated = creditProviders.reduce(
    (s, c) => s + Number(c.settlementAmount || 0),
    0,
  );
  const cashToCustomerItems = creditProviders.filter(
    (c) => c.paidTo === PAID_TO_CUSTOMER,
  );
  const cashToCustomerTotal = cashToCustomerItems.reduce(
    (s, c) => s + Number(c.settlementAmount || 0),
    0,
  );
  const remainingCash = loanAmount - totalConsolidated;
  return cashToCustomerTotal + Math.max(0, remainingCash);
};

/**
 * Parse credit providers for API
 */
export const parseCreditProvidersForApi = (
  creditProviders: ConsolidationItem[],
  cashOuts: Record<string, string | undefined>,
) =>
  creditProviders.map((c) => ({
    uuid: c.uuid,
    applicantUuid: c.applicant.uuid,
    provider: c.provider,
    sortCode: c.sortCode,
    accountNumber: c.accountNumber ?? "",
    settlementAmount: c.settlementAmount,
    reference: c.reference,
    paidTo: c.paidTo,
    purpose: cashOuts[`settle-${c.uuid}`] ?? "",
  }));

/**
 * Get the left over cash purpose
 */
export const getLeftOverCashPurpose = (
  cashOuts: Record<string, string | undefined>,
) =>
  Object.keys(cashOuts)
    .filter((c) => c === "remaining")
    .map((c) => cashOuts[c])
    .join(", ");
